import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import Text from '$tablet-app/src/app/components/Text';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { sticky_note_color_choices } from '@akin-home/sticky-notes';
import {
  useDeleteStickyNote,
  useListHabitatStickyNotes,
  useQuickUpdateStickyNote,
} from '@akin-home/sticky-notes/react';
import React, { useState } from 'react';
import { useWindowDimensions } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import styled from 'styled-components/native';
import { IndicatorDots } from '../../components/IndicatorDots';
import { useCurrentUser, useListCurrentHabitatUsers } from '../../users';
import {
  filterStickyNotesForUser,
  setStickyNoteAcknowledgement,
} from '../functions';
import useAvatarStickyNote from '../functions/useAvatarStickyNote';
import { StickyNote } from '../types';
import ManageStickyNoteDialog from './ManageStickyNoteDialog';
import StickyNoteSheet from './StickyNoteSheet';

interface Props {
  width: number;
}

const StickyNoteDashboardTile = ({ width }: Props) => {
  const { data: users } = useListCurrentHabitatUsers();
  const user = useCurrentUser();
  const { habitatID } = user;

  const { sticky_notes: notes, isLoading } =
    useListHabitatStickyNotes(habitatID);

  const avatar_sticky_note = useAvatarStickyNote();

  const sticky_notes = [
    ...filterStickyNotesForUser(notes, user),
    avatar_sticky_note,
  ];

  const update = useQuickUpdateStickyNote();
  const deleteNote = useDeleteStickyNote();

  const [stickyNoteVisible, showManageStickyNote, hideManageStickyNote] =
    useBool();

  const [editingNote, setEditingNote] = useState<StickyNote | undefined>();

  const onClick = () => {
    setEditingNote(undefined);
    showManageStickyNote();
  };

  const onPressDelete = ({ id }: StickyNote) => deleteNote(id);

  const onPressEdit = (sticky_note: StickyNote) => {
    setEditingNote(sticky_note);
    showManageStickyNote();
  };

  const onPressThumb = (sticky_note: StickyNote) => {
    update({
      id: sticky_note.id,
      acknowledgements: setStickyNoteAcknowledgement(sticky_note, user.id)
        .acknowledgements,
    });
  };

  const handleCloseEditingStickyNote = () => {
    setEditingNote(undefined);
    hideManageStickyNote();
  };

  const sticky_note_count = sticky_notes.length;

  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const isSmallScreen = screenWidth < 600 || screenHeight < 600;

  return (
    <Container>
      {isLoading ? (
        <FullScreenLoader />
      ) : !isLoading && sticky_note_count === 0 ? (
        <Placeholder onPress={onClick} disabled={isSmallScreen}>
          <PlaceholderText variant="stickyNote">
            {isSmallScreen
              ? 'Add a sticky note on your tablet!'
              : 'Press here to add a sticky note!'}
          </PlaceholderText>
        </Placeholder>
      ) : (
        <Carousel
          data={sticky_notes}
          width={width - 16}
          loop
          mode={sticky_note_count > 3 ? 'vertical-stack' : undefined}
          modeConfig={{ showLength: sticky_note_count - 1 || 1 }}
          renderItem={({ item, index }) => (
            <StickyNoteSheet
              sticky_note={item}
              style={{ width: width - 16 }}
              disabled={isSmallScreen}
              current_user={user}
              users={users}
              onPress={onClick}
              onPressEdit={!isSmallScreen ? onPressEdit : undefined}
              onPressAcknowledge={!item.ai_generated ? onPressThumb : undefined}
              onPressDelete={onPressDelete}
              bottom_left={
                <IndicatorDots
                  count={sticky_notes.length}
                  active_index={index}
                />
              }
            />
          )}
        />
      )}
      {stickyNoteVisible && !isSmallScreen ? (
        <ManageStickyNoteDialog
          onEditClose={handleCloseEditingStickyNote}
          editingNote={editingNote}
        />
      ) : null}
    </Container>
  );
};

const Container = styled.View`
  flex: 1;
`;

const Placeholder = styled.TouchableOpacity`
  flex: 1;
  padding: 20px;
  justify-content: center;
  border-radius: 22px;
  background-color: ${sticky_note_color_choices[0]};
`;

const PlaceholderText = styled(Text)`
  text-align: center;
`;

export default StickyNoteDashboardTile;
